// Dispose of stock that has been set aside: everything in the pending-disposal
// containers is listed, all of it ticked, and the waste transfer note (number and
// scan) and who handled it are recorded with it. POST /stock/dispose; the rules are
// core/stock.dispose — only stock in a pending-disposal container can go, so nothing
// leaves a bag straight into the bin without a record of why it was set aside.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from "@mui/material";
import { API_BASE_URL } from "../../config";
import { notify } from "../../notify";
import { allHoldings, placeLabel, stockRoleOf } from "../../stockRules";
import StockDocumentField from "./StockDocumentField";

export default function DisposeStockDialog({ open, onClose, onDone, items, types, me }) {
  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.id, t])), [types]);
  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);
  const pending = useMemo(() => allHoldings(items)
    .filter(h => stockRoleOf(h.item, itemById, typeById) === "quarantine"),
                         [items, itemById, typeById]);

  const [pick, setPick] = useState({});   // holding id -> quantity as typed, or absent
  const [handledBy, setHandledBy] = useState("");
  const [reference, setReference] = useState("");
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPick(Object.fromEntries(pending.map(h => [h.id, h.quantity])));
    setHandledBy(me?.name || ""); setReference(""); setDoc(null); setNotes("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = pending.filter(h => pick[h.id] !== undefined);
  const bad = chosen.some(h => !(Number(pick[h.id]) >= 1) || Number(pick[h.id]) > h.quantity);
  const canSave = chosen.length > 0 && !bad && handledBy.trim() && !saving;

  const save = () => {
    setSaving(true);
    axios.post(`${API_BASE_URL}/stock/dispose`, {
      handled_by: handledBy.trim(),
      reference: reference.trim() || null,
      document_id: doc?.id || null,
      notes: notes.trim() || null,
      lines: chosen.map(h => ({ holding_id: h.id, quantity: Number(pick[h.id]) })),
    }).then(() => {
      notify("Disposal recorded.", "success");
      onDone && onDone();
      onClose();
    }).catch(() => {}).finally(() => setSaving(false));
  };

  const toggle = (h) => setPick(p => {
    const next = { ...p };
    if (next[h.id] === undefined) next[h.id] = h.quantity; else delete next[h.id];
    return next;
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Dispose of stock</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Records stock leaving for safe disposal. Only what has been set aside in a
            pending-disposal container is listed.
          </DialogContentText>
          {pending.length === 0 ? (
            <Alert severity="info">Nothing is waiting to be disposed of.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                  <TableCell padding="checkbox" />
                  <TableCell>Item</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="right">Held</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Dispose of</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.map(h => (
                  <TableRow key={h.id}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={pick[h.id] !== undefined} onChange={() => toggle(h)} />
                    </TableCell>
                    <TableCell>{placeLabel(h.item, itemById, typeById)}</TableCell>
                    <TableCell>{h.serial_number || "—"}</TableCell>
                    <TableCell>{h.expiry_date || "—"}</TableCell>
                    <TableCell align="right">{h.quantity}</TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" sx={{ width: 90 }}
                                 disabled={pick[h.id] === undefined}
                                 value={pick[h.id] ?? ""}
                                 error={pick[h.id] !== undefined
                                        && (!(Number(pick[h.id]) >= 1) || Number(pick[h.id]) > h.quantity)}
                                 slotProps={{ htmlInput: { min: 1, max: h.quantity, step: 1 } }}
                                 onChange={e => setPick(p => ({ ...p, [h.id]: e.target.value }))} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: { sm: "flex-start" } }}>
            <TextField label="Handled by" required value={handledBy} sx={{ flex: 1 }}
                       helperText="Who took it for disposal"
                       onChange={e => setHandledBy(e.target.value)} />
            <TextField label="Waste transfer note number" value={reference} sx={{ flex: 1 }}
                       onChange={e => setReference(e.target.value)} />
          </Stack>
          <StockDocumentField category="waste_transfer_note" label="Attach waste transfer note"
                              value={doc} onChange={setDoc} />
          <TextField label="Notes" fullWidth multiline value={notes}
                     onChange={e => setNotes(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={!canSave} onClick={save}>
          Record disposal
        </Button>
      </DialogActions>
    </Dialog>
  );
}
